import moment from 'moment';
import { writeFile, utils } from 'xlsx-js-style';
import db from '@utils/db';

// Faithful port of UnmanagedApi's Export_CourseStatistic.ts (excel4node, server-side) to
// xlsx-js-style (browser-side), following the same db-calling-utility pattern as pdfTools.ts's
// GeneratePdf. Orchestrates the same calls the middle layer used to make itself
// (ADM_CourseStat2Excel, conditionally Check4NotFulldays + two GetPause4Students calls for the
// pause-adjusted percentage math, then ADM_SubjectOnCourseStat2Excel) - all now direct-called and
// role-checked (AdminGroupName) instead of going through a shared static ApiKey.

const styleNormal = { font: { color: { rgb: '000000' }, sz: 10 }, numFmt: '##0.0;' };
const styleBold = { font: { color: { rgb: '000000' }, sz: 10, bold: true }, numFmt: '##0.0;' };
const styleDuration = { font: { color: { rgb: '000000' }, sz: 10 }, numFmt: '0.0' };

function setString(ws: any, row: number, col: number, value: string, style: any) {
  ws[utils.encode_cell({ r: row, c: col })] = { t: 's', v: value ?? '', s: style };
}

function setNumber(ws: any, row: number, col: number, value: number, style: any) {
  ws[utils.encode_cell({ r: row, c: col })] = { t: 'n', v: value, s: style };
}

function showAbsenceOrAttendance(attendance: boolean, percentage: number): number {
  return attendance === true ? 100 - percentage : percentage;
}

function toPercentageNumber(value: string | number): number {
  return parseFloat(String(value).replace(',', '.'));
}

export interface CourseStatisticExportText {
  filename: string;
  tabname: string;
  col1: string;
  col2: string;
  col3: string;
  col4: string;
  col5: string;
  duration: string;
  courseDuration: string;
  totalDuration: string;
  absenceDuration: string;
  courseAbsenceDuration: string;
  totalAbsenceDuration: string;
  percentageLabel: string;
  subject?: string;
}

export async function ExportCourseStatisticExcel(
  courseId: string,
  fromDate: moment.Moment,
  toDate: moment.Moment,
  exportText: CourseStatisticExportText,
  useMerit: boolean,
  usePause: boolean,
  showPresence: boolean,
  includeDurations: boolean,
) {
  if (!courseId) return Promise.reject('No courseId provided');
  const formattedFromDate = fromDate.format('YYYY-MM-DD');
  const formattedToDate = toDate.format('YYYY-MM-DD');

  const data = await db.ADM_CourseStat2Excel(courseId, formattedFromDate, formattedToDate, useMerit, usePause);
  if (!data) return;

  if (usePause) {
    const notFullDays = await db.Check4NotFulldays();
    if (notFullDays?.length > 0 && notFullDays[0].active) {
      const studentIdsJson = JSON.stringify(data.map((s: any) => ({ student_id: s.student_sis_id })));

      const pauseStudentsTotal = await db.GetPause4Students(studentIdsJson, formattedFromDate, formattedToDate);
      const pauseStudentsCourse = await db.GetPause4Students(studentIdsJson, formattedFromDate, formattedToDate, courseId);

      for (const s of pauseStudentsTotal ?? []) {
        const student = data.find((st: any) => st.student_sis_id === s.student_id);
        if (student) {
          student.total_duration = student.total_duration - s.pause_length;
          student.total_absenceduration = student.total_absenceduration - s.pause_absence_length;
          student.percentage_total = ((student.total_absenceduration / student.total_duration) * 100).toString();
        }
      }

      for (const s of pauseStudentsCourse ?? []) {
        const student = data.find((st: any) => st.student_sis_id === s.student_id);
        if (student) {
          student.course_duration = student.course_duration - s.pause_length;
          student.course_absenceduration = student.course_absenceduration - s.pause_absence_length;
          student.percentage = ((student.course_absenceduration / student.course_duration) * 100).toString();
        }
      }
    }
  }

  const workbook = utils.book_new();
  const mainSheet = buildMainSheet(exportText, data, showPresence, includeDurations);
  utils.book_append_sheet(workbook, mainSheet, exportText.tabname.substr(0, 31));

  const subjectData = await db
    .ADM_SubjectOnCourseStat2Excel(courseId, formattedFromDate, formattedToDate, null, useMerit, usePause)
    .catch(() => null);

  if (subjectData?.length > 0) {
    const subjectSheet = buildSubjectSheet(exportText, subjectData, showPresence, includeDurations);
    utils.book_append_sheet(workbook, subjectSheet, (exportText.subject || 'Fag').substr(0, 31));
  }

  writeFile(workbook, exportText.filename + '.xlsx');
}

function buildMainSheet(exportText: CourseStatisticExportText, data: any[], showPresence: boolean, includeDurations: boolean) {
  const ws: any = {};

  setString(ws, 0, 0, exportText.col1, styleBold);
  setString(ws, 0, 1, exportText.col2, styleBold);
  setString(ws, 0, 2, exportText.col3, styleBold);
  setString(ws, 0, 3, `${exportText.col4} (${exportText.percentageLabel})`, styleBold);
  setString(ws, 0, 4, `${exportText.col5} (${exportText.percentageLabel})`, styleBold);
  if (includeDurations) {
    setString(ws, 0, 5, `${exportText.courseDuration} (min)`, styleBold);
    setString(ws, 0, 6, `${exportText.totalDuration} (min)`, styleBold);
    setString(ws, 0, 7, `${exportText.courseAbsenceDuration} (min)`, styleBold);
    setString(ws, 0, 8, `${exportText.totalAbsenceDuration} (min)`, styleBold);
  }

  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    const rowIndex = i + 1;
    setString(ws, rowIndex, 0, row.username, styleNormal);
    setString(ws, rowIndex, 1, row.displayname, styleNormal);
    setString(ws, rowIndex, 2, row.class_name, styleNormal);
    setNumber(ws, rowIndex, 3, showAbsenceOrAttendance(showPresence, toPercentageNumber(row.percentage)), styleNormal);
    setNumber(ws, rowIndex, 4, showAbsenceOrAttendance(showPresence, toPercentageNumber(row.percentage_total)), styleNormal);
    if (includeDurations) {
      setNumber(ws, rowIndex, 5, Number(row.course_duration) / 60, styleDuration);
      setNumber(ws, rowIndex, 6, Number(row.total_duration) / 60, styleDuration);
      setNumber(ws, rowIndex, 7, Number(row.course_absenceduration) / 60, styleDuration);
      setNumber(ws, rowIndex, 8, Number(row.total_absenceduration) / 60, styleDuration);
    }
  }

  ws['!ref'] = `A1:${includeDurations ? 'I' : 'E'}${Math.max(data.length + 1, 1)}`;
  // Every column needs a width: an empty {} (meant as "Excel's default") is written as a width-less
  // column, which Excel shows 0 wide - the username column looked missing.
  ws['!cols'] = includeDurations
    ? [{ wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 30 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 20 }]
    : [{ wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 30 }, { wch: 30 }];
  return ws;
}

function buildSubjectSheet(exportText: CourseStatisticExportText, subjectData: any[], showPresence: boolean, includeDurations: boolean) {
  const ws: any = {};

  const subjects: string[] = [];
  const students: Record<string, any> = {};

  for (const s of subjectData) {
    if (!subjects.includes(s.coursedescription)) subjects.push(s.coursedescription);
    const student = students[s.student_sis_id] ?? { username: s.username, displayname: s.displayname, student_sis_id: s.student_sis_id };
    student[s.coursedescription] = { lessonduration: s.lessonduration, absenceduration: s.absenceduration, percentage: s.percentage };
    students[s.student_sis_id] = student;
  }

  setString(ws, 0, 0, exportText.col1, styleBold);
  setString(ws, 0, 1, exportText.col2, styleBold);
  subjects.forEach((subject, index) => {
    const firstColumn = 2 + index * (includeDurations ? 3 : 1);
    setString(ws, 0, firstColumn, `${subject} (${exportText.percentageLabel})`, styleBold);
    if (includeDurations) {
      setString(ws, 0, firstColumn + 1, `${subject} (${exportText.duration}, min)`, styleBold);
      setString(ws, 0, firstColumn + 2, `${subject} (${exportText.absenceDuration}, min)`, styleBold);
    }
  });

  const sortedStudents = Object.values(students).sort((a: any, b: any) => {
    if (a.username > b.username) return 1;
    if (a.username < b.username) return -1;
    return 0;
  });

  for (let i = 0; i < sortedStudents.length; i++) {
    const student: any = sortedStudents[i];
    const rowIndex = i + 1;
    setString(ws, rowIndex, 0, student.username, styleNormal);
    setString(ws, rowIndex, 1, student.displayname, styleNormal);
    subjects.forEach((subject, x) => {
      const entry = student[subject];
      const firstColumn = 2 + x * (includeDurations ? 3 : 1);
      if (entry) {
        setNumber(ws, rowIndex, firstColumn, showAbsenceOrAttendance(showPresence, toPercentageNumber(entry.percentage)), styleNormal);
        if (includeDurations) {
          setNumber(ws, rowIndex, firstColumn + 1, Number(entry.lessonduration) / 60, styleDuration);
          setNumber(ws, rowIndex, firstColumn + 2, Number(entry.absenceduration) / 60, styleDuration);
        }
      } else {
        setString(ws, rowIndex, firstColumn, '-', styleNormal);
        if (includeDurations) {
          setString(ws, rowIndex, firstColumn + 1, '-', styleNormal);
          setString(ws, rowIndex, firstColumn + 2, '-', styleNormal);
        }
      }
    });
  }

  const lastCol = utils.encode_col(1 + subjects.length * (includeDurations ? 3 : 1));
  ws['!ref'] = `A1:${lastCol}${Math.max(sortedStudents.length + 1, 1)}`;
  ws['!cols'] = [
    { wch: 20 }, // not {} - see buildMainSheet
    { wch: 30 },
    ...subjects.flatMap(() => (includeDurations ? [{ wch: 34 }, { wch: 18 }, { wch: 22 }] : [{ wch: 34 }])),
  ];
  return ws;
}
